import type {
  CabinetAppointmentView,
  ClientListItem,
  ScheduleResult,
} from "@planerius/contracts/cabinet"
import type { ReactNode } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useCabinetAccess, useHasPermission } from "../../access/use-access"
import type { JournalQuery } from "../../api/journal.api"
import { releaseSlotHold } from "../../api/journal-actions.api"
import { isApiProblem } from "../../api/problem"
import type { AppointmentBrief } from "../../journal/appointment-dialogs"
import {
  BlockTimeDialog,
  CancelAppointmentDialog,
  ConfirmWarningsDialog,
  NoShowDialog,
  RescheduleDialog,
  VersionConflictDialog,
} from "../../journal/appointment-dialogs"
import { AppointmentPanel } from "../../journal/appointment-panel"
import type { OpenSlot } from "../../journal/booking-draft"
import { BookingForm } from "../../journal/booking-form"
import { lateCancelNote } from "../../journal/consequences"
import { freeWindows } from "../../journal/free-slots"
import { JournalDayList } from "../../journal/journal-day-list"
import { JournalGrid } from "../../journal/journal-grid"
import { buildJournalDay } from "../../journal/journal-model"
import { JournalWeekGrid } from "../../journal/journal-week-grid"
import { buildJournalWeek, weekSummary } from "../../journal/journal-week"
import { formatWeekRange, weekDates, weekStart } from "../directories/staff/schedule-model"
import { journalAvailability, parsePlatformState, platformFromProblem } from "../../journal/journal-mode"
import { useCachedAt } from "../../offline/register"
import { JournalEmpty, JournalSkeleton, StaleDataStripe } from "../../journal/journal-states"
import { formatDuration, formatMinute, instantFromLocal, minutesOfDay } from "../../journal/journal-time"
import styles from "../../journal/journal-actions.module.css"
import { formatMoney } from "../../journal/money"
import { useNowMinute } from "../../journal/use-journal-clock"
import { useIsPhone } from "../../journal/use-viewport"
import { cx } from "../../lib/cx"
import {
  formatDateLong,
  formatDayMonth,
  formatTimeOfDay,
  shiftDate,
  todayInZone,
  viewerTimeZone,
} from "../../lib/format"
import { fetchJournal } from "../../api/journal.api"
import { reportDataAt } from "../../shell/data-freshness"
import { ProblemScreen } from "../../shell/states"
import { useOnline } from "../../shell/use-online"
import { branchSubstitution, rememberBranch, resolveBranch } from "../../shell/use-branch"
import { Button } from "../../ui/button"
import type { JournalSearch, JournalStep, JournalView } from "./journal-search"
import { JOURNAL_STEPS, SLOT_NO_MASTER } from "./journal-search"
import screen from "./journal-screen.module.css"
import { useBookingSession } from "./use-booking-session"
import { useConsentDate } from "./use-consent-date"
import type { BookingStep } from "../../journal/booking-steps"
import { useCancelReasons } from "./use-cancel-reasons"
import { useAppointmentLookup } from "./use-appointment-lookup"
import { useJournalDay } from "./use-journal-day"
import { useJournalActions } from "./use-journal-actions"

/**
 * Журнал дня (Э2.1) вместе с действиями П0.
 *
 * Экран, за которым администратор проводит смену целиком. Он отвечает за четыре вещи:
 * что запрошено у сервера, что из этого состояние экрана, КАКАЯ раскладка показана
 * и ЧТО СЕЙЧАС ОТКРЫТО ПОВЕРХ ДНЯ. Последнее живёт в адресе, а не в состоянии
 * компонента: ссылку на открытую запись отправляют в поддержку.
 *
 * ТЕЛЕФОН И НОУТБУК — РАЗНЫЕ РАСКЛАДКИ, А НЕ ОДНА АДАПТИВНАЯ (ТЗ §2.13). На 360 сетка
 * не сжимается: колонка мастера имеет пол в 150 px, и «сжатая» сетка — это две колонки
 * из шести. Вместо неё показывается день одного мастера списком.
 *
 * ГДЕ ЗДЕСЬ ГРАНИЦА. Экран решает, ЧТО показать; как это выглядит — знают компоненты
 * из src/journal, а что при этом разрешено — чистая функция journalAvailability.
 * Правило «в офлайне ничего не меняем» проверяется расчётом, а не глазами по снимку.
 */
export function JournalScreen(props: {
  readonly search: JournalSearch
  readonly onSearchChange: (patch: JournalSearch) => void
  /** Печать дня (2-2.12): день и филиал, которые сейчас открыты. */
  readonly onPrint?: ((date: string, branchId: string) => void) | undefined
}): ReactNode {
  const { context } = useCabinetAccess()
  const canAddStaff = useHasPermission("staff.manage")
  const canManageShifts = useHasPermission("staff.schedule.manage")
  const canCreate = useHasPermission("appointments.create")
  const canSetStatus = useHasPermission("appointments.status.set")
  const canReschedule = useHasPermission("appointments.reschedule")
  const canCancel = useHasPermission("appointments.cancel")
  const canSeePhone = useHasPermission("clients.phone.view")
  const canRestrictClient = useHasPermission("clients.blacklist.manage")
  const canHold = useHasPermission("holds.manage")
  const canBusy = useHasPermission("busy.manage")
  const isPhone = useIsPhone()
  const online = useOnline()

  const branch = resolveBranch(context, props.search.branch ?? null)

  /*
   * Ссылку с `?branch=` присылает коллега. Если этот филиал человеку не открыт, журнал
   * показывает другой — и человек ищет в нём присланную запись, не находит и звонит
   * в поддержку. Подмену надо назвать словами (1-Э1.11).
   */
  const substitution = branchSubstitution(context, props.search.branch ?? null)
  const timeZone = branch?.timezone ?? context.tenant.defaultTimezone

  // День считается по зоне ФИЛИАЛА, а не устройства: «сегодня» во Владивостоке наступает
  // раньше московского, и день, взятый у браузера, уехал бы на сутки.
  const date = props.search.date ?? todayInZone(timeZone)
  const step: JournalStep = props.search.step ?? 15

  /*
   * ВИД ЖИВЁТ В АДРЕСЕ. Поле `view` парсилось с самого начала и не читалось никем:
   * `?view=week` молча рисовал день. Теперь вид решает и то, сколько дней запросить,
   * и то, что нарисовать, — а ссылка на неделю пересылается коллеге и открывается
   * неделей.
   *
   * На телефоне недели нет: сетка семь дней × получасы на 360 px даёт клетки в 6 px.
   * Телефон остаётся лентой дня (5-А1), и это не урезание, а другой экран.
   */
  const view: JournalView = isPhone ? "day" : (props.search.view ?? "day")
  const isWeek = view === "week" || view === "master"
  const weekFrom = weekStart(date)
  const weekList = useMemo(() => weekDates(weekFrom), [weekFrom])

  const query = useMemo<JournalQuery | null>(() => {
    if (branch === null) return null
    return {
      branchId: branch.id,
      // Неделя — ОДИН запрос на семь дней: контракт разрешает диапазон до 31 дня,
      // и семь отдельных запросов дали бы семь ожиданий вместо одного.
      dateFrom: isWeek ? weekFrom : date,
      dateTo: isWeek ? (weekList[6] ?? date) : date,
      employeeIds: props.search.masters,
      /*
       * Отменённые записи запрашиваются ещё и тогда, когда в адресе стоит конкретная
       * запись. Иначе ссылка «посмотри, что с этим визитом», присланная в поддержку,
       * открывает пустой день: визит уже отменён, сервер его не прислал, и показать
       * нечего. На сетку это не влияет — её фильтр работает отдельно, в buildJournalDay.
       */
      includeCancelled: (props.search.cancelled ?? false) || props.search.appointment !== undefined,
      viewerTimeZone: viewerTimeZone(),
    }
  }, [
    branch,
    date,
    isWeek,
    weekFrom,
    weekList,
    props.search.masters,
    props.search.cancelled,
    props.search.appointment,
  ])

  const day = useJournalDay(query)
  const result = day.result
  const nowMinute = useNowMinute(timeZone, date)

  const model = useMemo(
    () =>
      result === null
        ? null
        : buildJournalDay(result, date, {
            includeCancelled: props.search.cancelled ?? false,
          }),
    [result, date, props.search.cancelled],
  )

  /*
   * НЕДЕЛЯ СТРОИТСЯ ПОВТОРНЫМ ВЫЗОВОМ РАСЧЁТА ДНЯ — тем самым, что рисует сетку дня.
   * Свой расчёт недели разошёлся бы с дневным, и «свободно» на двух соседних экранах
   * назвало бы разные числа.
   */
  /*
   * НЕДЕЛЯ СТРОИТСЯ ТОЛЬКО ИЗ ОТВЕТА СВОЕГО ДИАПАЗОНА. В первый кадр после нажатия
   * «Неделя» в руках лежит ещё однодневный ответ, и неделя из него выходила такая:
   * шесть дней «никто не работает», подписанные уверенно, без признака загрузки.
   * Сверяем по дням ответа: пока в нём нет всех семи дат — показываем каркас.
   */
  const weekReady = useMemo(
    () =>
      result !== null && weekList.every((item) => result.days.some((row) => row.date === item)),
    [result, weekList],
  )

  /*
   * МАСТЕР ДЛЯ ПОДРЕЖИМА «ОДИН МАСТЕР» ПОДСТАВЛЯЕТСЯ САМ.
   *
   * Выбрать его на настольном экране было нечем: без `?master=` в адресе неделя мастера
   * показывала семь заштрихованных колонок, то есть кнопка «Один мастер» вела в пустоту.
   * Пока человек не выбрал сам, берём первого мастера со сменой на этой неделе — он
   * и есть самый вероятный ответ на вопрос «чью неделю посмотреть».
   */
  const weekMaster = useMemo(() => {
    if (props.search.master !== undefined) return props.search.master
    if (result === null || view !== "master") return null
    const withShift = result.employees.find((item) =>
      result.workdays.some((row) => row.employeeId === item.id),
    )
    return withShift?.id ?? result.employees[0]?.id ?? null
  }, [props.search.master, result, view])

  const week = useMemo(
    () =>
      result === null || !isWeek || !weekReady
        ? null
        : buildJournalWeek({
            result,
            dates: weekList,
            today: todayInZone(timeZone),
            mode: view === "master" ? "master" : "load",
            employeeId: weekMaster,
            includeCancelled: props.search.cancelled ?? false,
          }),
    [
      result,
      isWeek,
      weekReady,
      weekList,
      timeZone,
      view,
      weekMaster,
      props.search.cancelled,
    ],
  )

  const branchId = branch?.id ?? null
  useEffect(() => {
    if (branchId !== null) rememberBranch(context, branchId)
  }, [branchId, context])

  /*
   * Отметка «этот ответ отдан из кеша» приходит из единственной двери наружу
   * (api/http.ts → offline/register.ts). Ключ — адрес журнала: перелистывание дат и смена
   * фильтров идут тем же адресом, и отметка относится к последнему полученному ответу.
   */
  const cachedAt = useCachedAt("/cabinet/journal")

  // ─────────── что сейчас разрешено ───────────
  const availability = useMemo(
    () =>
      journalAvailability({
        online,
        problemCode: day.problem?.code ?? null,
        hasData: result !== null,
        platform:
          parsePlatformState(props.search.degraded) ??
          platformFromProblem(day.problem?.code ?? null),
        supportReadOnly: context.impersonation?.readOnly === true,
        /*
         * ВОЗРАСТ ДАННЫХ: момент СОХРАНЕНИЯ, если день пришёл из кеша, и момент сборки
         * сервером, если из сети. Подменять одно другим нельзя: `generatedAt` у ответа,
         * лежащего в кеше с утра, утренний — но человеку важно не «когда сервер посчитал»,
         * а «насколько старое я вижу», и это разные вопросы в тот день, когда кеш
         * достался от прошлого запуска браузера.
         */
        dataAt: cachedAt ?? result?.generatedAt ?? null,
        timeZone,
        // Пришёл ли день из кеша — говорит отметка service worker на ответе (П1-12).
        cached: cachedAt !== null && result !== null,
      }),
    [online, day.problem, result, props.search.degraded, context.impersonation, timeZone, cachedAt],
  )

  const [maintenanceDismissed, setMaintenanceDismissed] = useState(false)

  /*
   * «Данные на 14:35» в шапке — это про ТОТ ЖЕ день, на который человек смотрит.
   * Без этой строки оболочка показывала время загрузки профиля: полоса «Нет связи»
   * называла момент, к журналу отношения не имеющий, и успокаивала там, где не должна.
   */
  useEffect(() => {
    if (result === null) return
    // Полоса «Данные на 13:47» в шапке говорит о том же моменте, что и полоса режима:
    // два разных времени на одном экране человек читает как поломку.
    reportDataAt(cachedAt ?? result.generatedAt)
  }, [result, cachedAt])

  /*
   * Связь вернулась — день перечитывается сам. Администратор вышел из подвала с телефоном
   * в руке и первым делом видит правду о текущем часе, а не снимок, снятый до лифта.
   */
  const wasOffline = useRef(!online)
  useEffect(() => {
    if (online && wasOffline.current) day.reload()
    wasOffline.current = !online
  }, [online, day])

  const actions = useJournalActions({ onChanged: day.reload })

  /*
   * Снятие СВОЕГО удержания прямо из сетки (2-2.4.1). Отдельно от useJournalActions:
   * там живут действия над записью с ключом идемпотентности и версией карточки,
   * а здесь — одно необратимое «отпустить слот», у которого ни версии, ни повтора нет.
   *
   * Отказ не проглатывается: слот, который человек считает освобождённым, — это
   * обещание клиенту по телефону. День перечитывается в любом исходе, чтобы на экране
   * осталась правда, а не предположение.
   */
  /*
   * Обработчик адреса берётся из props на каждый рендер новым объектом, поэтому
   * в зависимостях эффектов он живёт ref-ом: иначе любой эффект, которому он нужен,
   * перезапускается на каждый рендер.
   */
  const onSearchChangeRef = useRef(props.onSearchChange)
  onSearchChangeRef.current = props.onSearchChange
  const onSearchChange = useCallback((next: JournalSearch): void => {
    onSearchChangeRef.current(next)
  }, [])

  const [holdFailure, setHoldFailure] = useState<string | null>(null)
  const releaseHold = useCallback(
    (holdId: string): void => {
      setHoldFailure(null)
      void (async () => {
        try {
          await releaseSlotHold(holdId)
          // Удачное снятие гасит прежний отказ: иначе текст «не удалось» висел
          // до перезагрузки страницы и рассказывал про беду, которой уже нет.
          setHoldFailure(null)
        } catch (error) {
          setHoldFailure(
            isApiProblem(error)
              ? `Удержание снять не удалось: ${error.title}`
              : "Удержание снять не удалось — проверьте связь.",
          )
        } finally {
          day.reload()
        }
      })()
    },
    [day],
  )
  const reasons = useCancelReasons(props.search.dialog === "cancel")

  // ─────────── что открыто поверх дня ───────────
  const appointments = result?.appointments ?? []
  const openAppointment =
    props.search.appointment === undefined
      ? null
      : (appointments.find((item) => item.id === props.search.appointment) ?? null)

  /*
   * ССЫЛКА НА ЗАПИСЬ ИЗ ДРУГОГО ДНЯ (1-Э1.14).
   *
   * `?appointment=<id>` присылают в поддержку и в переписку, а журнал грузит ОДИН день:
   * вчерашняя запись в нём не находится, панель не открывается, и экран молчит. Молчание
   * человек читает как «ссылка битая» и идёт искать запись руками. Поэтому, когда день
   * загружен, а записи в нём нет, спрашиваем сервер и переходим на её дату.
   *
   * Спрашиваем ТОЛЬКО после того, как день доехал: иначе запрос уходит на каждой первой
   * загрузке, ещё до того как выяснилось, что запись прямо здесь.
   */
  const lookup = useAppointmentLookup(
    props.search.appointment,
    openAppointment === null && result !== null && !day.refreshing,
  )

  /*
   * ПЕРЕХОД ДЕЛАЕТСЯ ОДИН РАЗ НА НОМЕР ЗАПИСИ. Без этого сторожа экран запирал человека
   * на дне записи: он жмёт стрелку «следующий день», адрес меняется, запись в новом дне
   * не находится, поиск повторяется и возвращает его обратно. Стрелки и «Сегодня»
   * переставали работать, пока в адресе стоит ?appointment=.
   */
  const jumpedTo = useRef<string | null>(null)
  useEffect(() => {
    const target = lookup.found
    if (target === null) return
    if (jumpedTo.current === target.appointment.id) return
    jumpedTo.current = target.appointment.id
    if (target.date === date && target.branchId === branchId) return
    onSearchChange({
      date: target.date,
      ...(target.branchId === branchId ? {} : { branch: target.branchId }),
    })
  }, [lookup.found, date, branchId, onSearchChange])

  const employeeNameOf = useCallback(
    (employeeId: string | null): string => {
      if (employeeId === null) return "Без мастера"
      return (
        result?.employees.find((item) => item.id === employeeId)?.displayName ?? "Мастер"
      )
    },
    [result],
  )

  const [prefill, setPrefill] = useState<OpenSlot["prefill"]>(undefined)

  const slot = useMemo<OpenSlot | null>(() => {
    if (props.search.slotAt === undefined) return null
    const [hours, minutes] = props.search.slotAt.split(":")
    const startMinute = Number(hours) * 60 + Number(minutes)
    const master = props.search.slotMaster
    return {
      date,
      startMinute,
      employeeId: master === undefined || master === SLOT_NO_MASTER ? null : master,
      /*
       * ДЛИТЕЛЬНОСТЬ БЕРЁТСЯ ИЗ ЗАГОТОВКИ, А НЕ ИЗ ШАГА СЕТКИ.
       *
       * «Продублировать» повторяет визит на час — а черновик получал пятнадцать
       * минут шага сетки: удержание вставало на четверть часа, интервал на экране
       * врал, и окна считались под чужую длительность. Пустая ячейка по-прежнему
       * открывается шагом сетки: там повторять нечего.
       */
      durationMinutes: prefill?.durationMinutes ?? step,
      ...(prefill === undefined ? {} : { prefill }),
    }
  }, [props.search.slotAt, props.search.slotMaster, date, step, prefill])

  const openSlot = useCallback(
    (employeeId: string | null, minute: number, withPrefill?: OpenSlot["prefill"]) => {
      setPrefill(withPrefill)
      props.onSearchChange({
        slotAt: formatMinute(minute),
        slotMaster: employeeId ?? SLOT_NO_MASTER,
        appointment: undefined,
        dialog: undefined,
      })
    },
    [props],
  )

  /*
   * Ячейка, на которой открыта форма, — карантинная? Тогда своего удержания на ней
   * не будет (слот уже закрыт занятостью карантина), и вместо отсчёта форма говорит,
   * до какого часа это время и так за салоном.
   */
  const quarantineNote = useMemo<string | null>(() => {
    if (slot === null || model === null || slot.employeeId === null) return null
    const column = model.columns.find((item) => item.employee.id === slot.employeeId)
    const quarantine = column?.holds.find(
      (item) =>
        item.isQuarantine &&
        item.startMinute <= slot.startMinute &&
        item.endMinute > slot.startMinute,
    )
    if (quarantine === undefined) return null
    return `Время за салоном до ${formatTimeOfDay(quarantine.expiresAt, timeZone)}`
  }, [slot, model, timeZone])

  const closeForm = useCallback(() => {
    // Форма СВОРАЧИВАЕТСЯ: из адреса уходит слот, а черновик остаётся в сеансе
    // и возвращается, если человек кликнет в то же самое место.
    props.onSearchChange({ slotAt: undefined, slotMaster: undefined })
  }, [props])

  /*
   * ШАГ ФОРМЫ ЖИВЁТ ЗДЕСЬ, А НЕ В ФОРМЕ (5-А2).
   *
   * По той же причине, что и черновик: состояние внутри компонента умирает вместе
   * с ним — при перерисовке после отказа, при смене дня, при выборе окна, который
   * двигает адрес. Человек, дошедший до пятого шага и получивший «слот заняли»,
   * не должен начинать с первого.
   */
  const [bookingStep, setBookingStep] = useState<BookingStep>(1)

  /*
   * ШАГИ НАЧИНАЮТСЯ СНАЧАЛА ТОЛЬКО ПРИ НОВОМ ОТКРЫТИИ ФОРМЫ.
   *
   * Сначала сброс был привязан к адресу слота — и выбор свободного окна, который
   * сам двигает адрес, выбрасывал человека с пятого шага на первый: он нажимал
   * предложенное время и вместо «Итога» видел поле телефона. Форма переживает
   * и смену дня, и выбор окна; заново разговор начинается, когда её ЗАКРЫЛИ
   * и открыли снова.
   */
  const formOpen = props.search.slotAt !== undefined
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (formOpen && !wasOpenRef.current) setBookingStep(1)
    wasOpenRef.current = formOpen
  }, [formOpen])

  const booking = useBookingSession({
    open: props.search.slotAt !== undefined,
    slot,
    branchId: branchId ?? "",
    timeZone,
    result,
    nowMinute,
    stepMinutes: result?.branch.slotStepMinutes ?? step,
    canEdit: availability.canEdit,
    canHold,
    employeeName: employeeNameOf,
    onCreated: () => {
      day.reload()
      props.onSearchChange({ slotAt: undefined, slotMaster: undefined })
    },
    // Слот придержан без записи: форма закрывается, а удержание остаётся и появляется
    // в сетке как своё — с подписью «держите вы · снять».
    onHeld: () => {
      props.onSearchChange({ slotAt: undefined, slotMaster: undefined })
      day.reload()
    },
    onSlotChanged: (next) => {
      props.onSearchChange({
        date: next.date,
        slotAt: formatMinute(next.startMinute),
        slotMaster: next.employeeId ?? SLOT_NO_MASTER,
      })
    },
  })

  const consentGrantedAt = useConsentDate({
    clientId: booking?.state.draft.clientId ?? null,
    active: bookingStep === 5,
    timeZone,
  })

  /*
   * ПОСЛЕ «ЭТО ВРЕМЯ ЗАНЯЛИ» ЧЕЛОВЕК ВОЗВРАЩАЕТСЯ НА ШАГ ВРЕМЕНИ, а не остаётся
   * на итоге, где нечего решать: заказ собран, не годится ровно время.
   */
  const hasConflict = booking?.state.conflict !== null && booking?.state.conflict !== undefined
  useEffect(() => {
    if (hasConflict) setBookingStep(4)
  }, [hasConflict])

  if (branch === null) {
    return (
      <div className={screen["screen"]}>
        <div className={screen["placeholder"]}>
          Вам не открыт ни один филиал этого салона. Попросите владельца выдать доступ —
          журнал появится сразу после этого.
        </div>
      </div>
    )
  }

  // Сбой ДО первых данных — это отдельный экран. Сбой поверх загруженного дня им не является:
  // день остаётся на экране, а сверху появляется полоса с его возрастом.
  if (day.problem !== null && result === null && availability.mode !== "maintenance") {
    return <ProblemScreen problem={day.problem} onRetry={day.reload} />
  }

  /*
   * «Только услуги Анны». Связка «кто что делает» лежит в списке сотрудников
   * (поле service_ids), а не в каталоге, поэтому пересечение считается здесь.
   * Если связки нет вовсе, показываются все услуги: пустой список в форме означал бы
   * «записать нельзя», а это неправда — это значит, что салон не отметил услуги мастера.
   */
  const services = booking?.catalog?.services ?? []
  const draftEmployeeId = booking?.state.draft.employeeId ?? null
  const employeeServices =
    draftEmployeeId === null
      ? null
      : (booking?.catalog?.employees.find((item) => item.id === draftEmployeeId) ?? null)
  const formServices =
    employeeServices === null || employeeServices.serviceIds.length === 0
      ? services
      : services.filter((item) => employeeServices.serviceIds.includes(item.id))

  /*
   * УСЛУГА, КОТОРОЙ У НОВОГО МАСТЕРА НЕТ, УБИРАЕТСЯ ИЗ ЧЕРНОВИКА.
   *
   * Список услуг фильтруется по выбранному мастеру, и после смены мастера выбранная
   * услуга просто исчезала с экрана — но оставалась в черновике и уезжала в запрос.
   * Администратор видел пустой выбор, нажимал «Записать» и получал запись с услугой,
   * которой не выбирал, либо отказ сервера «мастер не оказывает эту услугу».
   */
  const draftServiceIds = booking?.state.draft.serviceIds
  const bookingDispatch = booking?.dispatch
  useEffect(() => {
    if (draftServiceIds === undefined || bookingDispatch === undefined) return
    if (draftServiceIds.length === 0 || formServices.length === 0) return
    const kept = draftServiceIds.filter((id) => formServices.some((item) => item.id === id))
    if (kept.length === draftServiceIds.length) return
    bookingDispatch({
      type: "services",
      serviceIds: kept,
      durationMinutes: kept.reduce(
        (sum, id) => sum + (formServices.find((item) => item.id === id)?.durationMinutes ?? 0),
        0,
      ),
    })
  }, [draftServiceIds, formServices, bookingDispatch])

  /*
   * ПАНЕЛЬ ПОДПИСАНА ДАТОЙ САМОЙ ЗАПИСИ, А НЕ ДАТОЙ В ШАПКЕ.
   *
   * В неделе открытая карточка может относиться к любому из семи дней, а дата в шапке
   * — это начало недели. Панель показывала визит четверга подписанным понедельником,
   * и «Заблокировать время» отправляло на сервер эту чужую дату.
   */
  const brief =
    openAppointment === null
      ? null
      : briefOf(openAppointment, {
          employeeName: employeeNameOf(openAppointment.employeeId),
          date: todayInZone(timeZone, new Date(openAppointment.startAt)),
          timeZone,
        })

  return (
    <div className={screen["screen"]}>
      {availability.stripe === null ? null : (
        <div
          className={cx(
            styles["worksStripe"],
            availability.stripe.tone === "offline" ? styles["worksStop"] : undefined,
          )}
          role="status"
        >
          <span className={styles["worksTitle"]}>{availability.stripe.title}</span>
          <span className={styles["worksText"]}>{availability.stripe.text}</span>
          <button
            type="button"
            className={styles["worksAction"]}
            onClick={() => {
              day.reload()
            }}
          >
            {availability.stripe.actionLabel}
          </button>
        </div>
      )}

      {/*
        * ПАНЕЛЬ ЖУРНАЛА — ДВЕ СТРОКИ, КАК В МАКЕТЕ «Журнал - П0».
        *
        * Там навигация по дням, вид и «+ Новая запись» стоят в шапке, а шаг, отменённые
        * и сводка дня — в полосе под ней. Сложенные в одну полосу 38 px, они не помещались
        * даже на эталонных 1366: кнопки сжимались и ломали подписи на две строки
        * («Один / мастер», «+ Новая / запись»), а сводка обрезалась до «Свобо…».
        * Шапку оболочки журнал занять не может (она общая для всех разделов), поэтому
        * обе строки живут здесь, в том же порядке, что и в макете.
        */}
      <div className={screen["toolbar"]}>
        <div className={screen["toolbarRow"]}>
        <div className={screen["dateNav"]}>
          {/*
            * ШАГ СТРЕЛОК РАВЕН ПОКАЗАННОМУ ПЕРИОДУ. В неделе шаг в один день не менял
            * сетку вовсе: шесть нажатий подряд оставляли ту же самую неделю, и человек
            * решал, что кнопка сломана. И подпись обязана называть то, что показано:
            * одна дата над семью днями читается как «показан этот день».
            */}
          <button
            type="button"
            className={screen["dateStep"]}
            aria-label={isWeek ? "Предыдущая неделя" : "Предыдущий день"}
            onClick={() => {
              props.onSearchChange({ date: shiftDate(date, isWeek ? -7 : -1) })
            }}
          >
            ‹
          </button>
          <span className={screen["dateLabel"]}>
            {isWeek ? formatWeekRange(weekFrom) : formatDateLong(date, timeZone)}
          </span>
          <button
            type="button"
            className={screen["dateStep"]}
            aria-label={isWeek ? "Следующая неделя" : "Следующий день"}
            onClick={() => {
              props.onSearchChange({ date: shiftDate(date, isWeek ? 7 : 1) })
            }}
          >
            ›
          </button>
        </div>

        <button
          type="button"
          className={screen["today"]}
          onClick={() => {
            props.onSearchChange({ date: todayInZone(timeZone) })
          }}
        >
          Сегодня
        </button>

        {/*
          * ПЕРЕКЛЮЧАТЕЛЬ ВИДА. На телефоне его нет: недельная сетка там не помещается
          * (семь колонок по получасам дают клетки в 6 px), и кнопка, ведущая
          * в нечитаемое, хуже её отсутствия.
          */}
        {isPhone ? null : (
          <div className={screen["segmented"]} role="group" aria-label="Вид журнала">
            {(
              [
                { value: "day", label: "День" },
                { value: "week", label: "Неделя" },
                { value: "master", label: "Один мастер" },
              ] as const
            ).map((item) => (
              <button
                key={item.value}
                type="button"
                className={cx(
                  screen["segment"],
                  view === item.value ? screen["segmentActive"] : undefined,
                )}
                aria-pressed={view === item.value}
                onClick={() => {
                  props.onSearchChange({ view: item.value === "day" ? undefined : item.value })
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        {/*
          * ВЫБОР МАСТЕРА ВИДЕН ТОЛЬКО В ЕГО ПОДРЕЖИМЕ. В остальных видах это лишний
          * элемент: в «Дне» мастера стоят колонками, в «Неделе» показан весь салон.
          */}
        {isPhone || view !== "master" || result === null ? null : (
          <label className={screen["chips"]}>
            <span className={screen["chipLabel"]}>Мастер:</span>
            <select
              className={screen["chip"]}
              value={weekMaster ?? ""}
              onChange={(event) => {
                props.onSearchChange({ master: event.target.value })
              }}
            >
              {result.employees.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.displayName}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Кнопка создания прижата вправо первой строки — как в шапке макета. */}
        <span className={screen["toolbarSpacer"]} aria-hidden="true" />

        {/* Печать дня (2-2.12) — с компьютера: распечатку вешают у стойки, а не печатают с телефона. */}
        {isPhone || props.onPrint === undefined || branch === null ? null : (
          <Button size="sm" onClick={() => props.onPrint?.(date, branch.id)}>
            Печать
          </Button>
        )}

        {/*
         * «+ Новая запись» — кнопка создания для НАСТОЛЬНОЙ раскладки. На телефоне её
         * нет: там она стоит внизу, в зоне большого пальца, и знает выбранного мастера,
         * а верхняя записывала бы к первому в списке — к другому человеку, чем тот, чей
         * день открыт. Две кнопки с разным поведением и одной подписью учат не верить
         * обеим. В офлайне кнопка ВИДИМО недоступна и объясняет причину (ТЗ §2.11.4).
         */}
        {isPhone ? null : (
        <Button
          variant="primary"
          size="sm"
          deniedReason={
            canCreate
              ? (availability.deniedReason ?? undefined)
              : "Записывать клиентов может администратор салона."
          }
          onClick={() => {
            const firstMaster = model?.columns[0]?.employee.id ?? null
            openSlot(firstMaster, roundToStep(nowMinute, step))
          }}
        >
          + Новая запись
        </Button>
        )}
        </div>

        <div className={screen["toolbarRow"]}>
        {/*
          * ШАГ СЕТКИ И «ПОКАЗАТЬ ОТМЕНЁННЫЕ» — ТОЛЬКО НА ШИРОКОМ ЭКРАНЕ. На телефоне день
          * показан списком (макет 5-А1), и шаг сетки в нём не меняет ничего: переключатель,
          * от которого на экране ничего не происходит, — это две строки из шести сотен
          * пикселей, отданные под обещание. В макете телефона их нет по той же причине.
          */}
        {isPhone ? null : (
        <>
        <div className={screen["stepGroup"]}>
          <span className={screen["chipLabel"]}>Шаг сетки:</span>
          <div className={screen["segmented"]} role="group" aria-label="Шаг сетки">
            {JOURNAL_STEPS.map((value) => (
              <button
                key={value}
                type="button"
                className={cx(
                  screen["segment"],
                  value === step ? screen["segmentActive"] : undefined,
                )}
                aria-pressed={value === step}
                onClick={() => {
                  props.onSearchChange({ step: value })
                }}
              >
                {value} мин
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          className={cx(
            screen["chip"],
            props.search.cancelled === true ? screen["chipActive"] : undefined,
          )}
          aria-pressed={props.search.cancelled === true}
          onClick={() => {
            props.onSearchChange({ cancelled: props.search.cancelled !== true })
          }}
        >
          Показать отменённые
        </button>
        </>
        )}

        {/*
          * ОДНА СВОДКА НА ЭКРАН. Дневная строка рядом с недельной читалась как одно
          * целое: «Записи: 14» относилось к открытому дню, а стояло вплотную к итогу
          * недели — и число дня понималось как число недели.
          */}
        {isWeek ? (
          <span className={screen["summary"]}>
            {week === null ? (day.refreshing ? "Обновляем неделю…" : "") : weekSummary(week)}
          </span>
        ) : (
          <span className={screen["summary"]}>
            {model === null
              ? day.refreshing
                ? "Обновляем…"
                : ""
              : `Записи: ${String(model.summary.appointments)} · Ждут подтверждения: ${String(model.summary.pending)} · Свободно у смены: ${formatDuration(model.summary.freeMinutes)} · Неявки: ${String(model.summary.noShow)}`}
          </span>
        )}
        </div>
      </div>

      {/* Возраст данных показывается ВСЕГДА, а не только когда велик: без подписи
          устаревший день неотличим от актуального (ТЗ §2.11.4). */}
      {day.problem !== null && result !== null ? (
        <StaleDataStripe
          generatedAtLabel={formatTimeOfDay(result.generatedAt, timeZone)}
          onRetry={day.reload}
        />
      ) : null}

      {/*
        * Отказ снятия удержания — это про слот, который человек уже пообещал по телефону,
        * поэтому он стоит отдельной плашкой и закрывается рукой, а не живёт в подсказке
        * сетки: там он вытеснял бы «Записей на сегодня пока нет» и не гас бы никогда.
        */}
      {/*
        * Почему панель не открылась. Три разных ответа вместо одного молчания: запись
        * в другом филиале (проси доступ), запись не твоя (мастеру открыты свои),
        * записи больше нет (её отменили и убрали). Каждый ведёт к своему следующему шагу.
        */}
      {substitution === null ? null : (
        <div className={screen["holdFailure"]} role="status">
          <span>
            Филиал из ссылки не открыт вашей учётной записи. Показан «{substitution.openedName}» —
            записи присланного филиала здесь не будет.
          </span>
          <button
            type="button"
            className={screen["holdFailureClose"]}
            onClick={() => {
              props.onSearchChange({ branch: undefined })
            }}
          >
            Понятно
          </button>
        </div>
      )}

      {lookup.problem === null ? null : (
        <div className={screen["holdFailure"]} role="status">
          <span>
            {/*
              * Три разных ответа, а не один. «Её могли удалить» на обрыв связи —
              * это ложь, за которой человек перестанет искать настоящую причину.
              */}
            {lookup.problem.retryable
              ? "Не удалось проверить запись — похоже, пропала связь. Повторите через минуту."
              : lookup.problem.code === "branch_forbidden" ||
                  lookup.problem.code === "foreign_appointment"
                ? (lookup.problem.detail ?? lookup.problem.title)
                : "Такой записи в журнале нет: её могли удалить или ссылка ведёт в другой салон."}
          </span>
          <button
            type="button"
            className={screen["holdFailureClose"]}
            onClick={() => {
              props.onSearchChange({ appointment: undefined })
            }}
          >
            Журнал на сегодня
          </button>
        </div>
      )}

      {holdFailure === null ? null : (
        <div className={screen["holdFailure"]} role="alert">
          <span>{holdFailure}</span>
          <button
            type="button"
            className={screen["holdFailureClose"]}
            onClick={() => {
              setHoldFailure(null)
            }}
          >
            Понятно
          </button>
        </div>
      )}

      <div className={screen["work"]}>
        {/*
          * НЕДЕЛЯ ПРОВЕРЯЕТСЯ ДО ДНЕВНЫХ СОСТОЯНИЙ. «Филиал закрыт» и «никто
          * не работает» в неделе — свойство ОТДЕЛЬНОГО дня, а не всей недели:
          * закрытое воскресенье не повод не показывать остальные шесть дней.
          */}
        {isWeek ? (
          week === null ? (
            <JournalSkeleton />
          ) : (
            <JournalWeekGrid
              week={week}
              onOpenDay={(openDay) => {
                // Клик по клетке — главное действие недели: она отвечает «куда
                // поставить», а поставить можно только в дне.
                props.onSearchChange({ date: openDay, view: undefined })
              }}
              {...(availability.canOpenDetails
                ? {
                    onOpenCard: (appointmentId: string, cardDay: string) => {
                      props.onSearchChange({
                        date: cardDay,
                        view: undefined,
                        appointment: appointmentId,
                        dialog: undefined,
                      })
                    },
                  }
                : {})}
            />
          )
        ) : model === null ? (
          <JournalSkeleton />
        ) : model.window.isClosed ? (
          <JournalEmpty kind="closed" canManage={false} reason={model.window.exceptionReason} />
        ) : !model.hasEmployees ? (
          <JournalEmpty kind="no-employees" canManage={canAddStaff} />
        ) : !model.hasShifts ? (
          <JournalEmpty kind="no-shifts" canManage={canManageShifts} />
        ) : isPhone ? (
          <JournalDayList
            model={model}
            selectedEmployeeId={props.search.master ?? null}
            onSelectEmployee={(employeeId) => {
              props.onSearchChange({ master: employeeId ?? undefined })
            }}
            createDeniedReason={
              canCreate
                ? (availability.deniedReason ?? null)
                : "Записывать клиентов может администратор салона."
            }
            {...(availability.canOpenDetails
              ? {
                  onOpenCard: (cardId: string) => {
                    props.onSearchChange({
                      appointment: cardId,
                      dialog: undefined,
                      slotAt: undefined,
                      slotMaster: undefined,
                    })
                  },
                }
              : {})}
            {...(availability.canEdit && canCreate
              ? {
                  onPickSlot: (employeeId: string | null, minute: number) => {
                    openSlot(employeeId, minute)
                  },
                  onCreate: (employeeId: string | null) => {
                    // «Записать» без выбранного времени открывает форму на ближайшей
                    // будущей минуте по шагу филиала: администратор в зале чаще всего
                    // записывает «сейчас или чуть позже», а не на завтра.
                    openSlot(
                      employeeId,
                      Math.max(roundToStep(nowMinute, step), model.window.openMinute),
                    )
                  },
                }
              : {})}
          />
        ) : (
          <JournalGrid
            model={model}
            step={step}
            grayscale={props.search.check === "gray"}
            selectedCardId={props.search.appointment ?? null}
            actionsDisabled={!availability.canEdit}
            holdTtlSeconds={result?.rules.holdTtlSeconds ?? null}
            onRefresh={day.reload}
            {...(canHold && availability.canEdit ? { onReleaseHold: releaseHold } : {})}
            /*
             * Открыть запись и записать нового клиента — РАЗНЫЕ права и разные режимы.
             * В технических работах панель читается, а свободное место уже не кликается;
             * в офлайне выключено и то и другое (см. journal-mode.ts).
             */
            {...(availability.canOpenDetails
              ? {
                  onOpenCard: (cardId: string) => {
                    props.onSearchChange({
                      appointment: cardId,
                      dialog: undefined,
                      slotAt: undefined,
                      slotMaster: undefined,
                    })
                  },
                }
              : {})}
            {...(availability.canEdit && canCreate
              ? {
                  onPickSlot: (employeeId: string | null, minute: number) => {
                    openSlot(employeeId, minute)
                  },
                }
              : {})}
            {...(model.summary.appointments === 0
              ? {
                  hint: "Записей на сегодня пока нет. Кликните на свободное время, чтобы записать клиента.",
                }
              : {})}
          />
        )}

        {openAppointment === null ? null : (
          <AppointmentPanel
            appointment={openAppointment}
            employeeName={employeeNameOf(openAppointment.employeeId)}
            branchName={branch.name}
            timeZone={timeZone}
            date={date}
            now={Date.now()}
            canEdit={availability.canEdit}
            deniedReason={availability.deniedReason}
            {...(availability.canEdit
              ? {
                  onBlockTime: () => {
                    props.onSearchChange({ dialog: "block" })
                  },
                }
              : {})}
            permissions={{
              status: canSetStatus,
              reschedule: canReschedule,
              cancel: canCancel,
              busy: canBusy,
              create: canCreate,
              phone: canSeePhone,
            }}
            revealedPhone={actions.state.revealed[openAppointment.client.id] ?? null}
            revealing={actions.state.busy === "reveal"}
            busyAction={actions.state.busy}
            failure={actions.state.failure}
            callbacks={{
              onClose: () => {
                props.onSearchChange({ appointment: undefined, dialog: undefined })
              },
              onConfirm: () => {
                actions.confirm(openAppointment)
              },
              onArrived: () => {
                actions.arrived(openAppointment)
              },
              onCompleted: () => {
                actions.completed(openAppointment)
              },
              onUndoNoShow: () => {
                actions.undoNoShow(openAppointment)
              },
              onNoShow: () => {
                props.onSearchChange({ dialog: "no-show" })
              },
              onReschedule: () => {
                props.onSearchChange({ dialog: "reschedule" })
              },
              onCancel: () => {
                props.onSearchChange({ dialog: "cancel" })
              },
              onDuplicate: () => {
                openSlot(openAppointment.employeeId, minuteOfEnd(openAppointment, timeZone), {
                  client: clientOf(openAppointment),
                  durationMinutes: durationOf(openAppointment, timeZone),
                  // Дублируем ВЕСЬ состав визита: «стрижка и борода» повторяется
                  // целиком, а не одной первой услугой.
                  ...(openAppointment.services.length === 0
                    ? {}
                    : { serviceIds: openAppointment.services.map((item) => item.serviceId) }),
                  ...(openAppointment.comment === null
                    ? {}
                    : { comment: openAppointment.comment }),
                })
              },
              onRevealPhone: () => {
                actions.revealPhone(openAppointment)
              },
            }}
          />
        )}

        {booking === null || slot === null ? null : (
          <BookingForm
            state={booking.state}
            dispatch={booking.dispatch}
            services={formServices}
            employees={result?.employees ?? []}
            employeeName={employeeNameOf(booking.state.draft.employeeId)}
            catalogFailed={booking.catalogFailed}
            catalogLoading={booking.catalog === null && !booking.catalogFailed}
            forAnyMaster={draftEmployeeId === null}
            holdSecondsLeft={booking.holdSecondsLeft}
            holdTtlSeconds={result?.rules.holdTtlSeconds ?? null}
            quarantineNote={quarantineNote}
            canEdit={availability.canEdit}
            deniedReason={availability.deniedReason}
            steps={isPhone}
            step={bookingStep}
            dayWindows={booking.dayWindows}
            today={todayInZone(timeZone)}
            consentGrantedAt={consentGrantedAt}
            top={68}
            left={80}
            callbacks={{
              onClose: closeForm,
              onSubmit: booking.submit,
              onPickWindow: booking.pickWindow,
              onRetryWindows: booking.retryWindows,
              onKeepHold: booking.keepHold,
              onRefresh: day.reload,
              onPickDay: booking.pickDay,
              onStep: setBookingStep,
            }}
          />
        )}

        {props.search.dialog === "cancel" && openAppointment !== null && brief !== null && result !== null ? (
          <CancelAppointmentDialog
            appointment={brief}
            lateNote={lateCancelNote({
              startAt: openAppointment.startAt,
              cancelDeadlineMinutes: result.rules.cancelDeadlineMinutes,
              allowOnlineCancel: result.rules.allowOnlineCancel,
              timeZone,
              nowMs: Date.now(),
            })}
            reasons={reasons}
            rules={result.rules}
            submitting={actions.state.busy === "cancel"}
            failure={actions.state.failure}
            onKeep={() => {
              props.onSearchChange({ dialog: undefined })
            }}
            onSubmit={(value) => {
              actions.cancel(openAppointment, value)
              props.onSearchChange({ dialog: undefined })
            }}
          />
        ) : null}

        {props.search.dialog === "no-show" && openAppointment !== null && brief !== null && result !== null ? (
          <NoShowDialog
            appointment={brief}
            rules={result.rules}
            canRestrict={canRestrictClient}
            submitting={actions.state.busy === "no-show"}
            failure={actions.state.failure}
            onBack={() => {
              props.onSearchChange({ dialog: undefined })
            }}
            onSubmit={(value) => {
              actions.noShow(openAppointment, value)
              props.onSearchChange({ dialog: undefined })
            }}
          />
        ) : null}

        {props.search.dialog === "block" && openAppointment !== null && result !== null ? (
          <BlockTimeDialog
            title="Заблокировать время"
            dateLabel={formatDateLong(date, timeZone)}
            employeeName={employeeNameOf(openAppointment.employeeId)}
            startMinute={minutesOfDay(new Date(openAppointment.startAt), timeZone)}
            endMinute={minutesOfDay(new Date(openAppointment.endAt), timeZone)}
            submitting={actions.state.busy === "block"}
            failure={actions.state.failure}
            onBack={() => {
              props.onSearchChange({ dialog: undefined })
            }}
            onSubmit={(value) => {
              actions.blockTime({
                branchId: result.branch.id,
                kind: value.kind,
                ...(openAppointment.employeeId === null
                  ? {}
                  : { employeeId: openAppointment.employeeId }),
                startAt: instantFromLocal(date, value.startMinute, timeZone),
                endAt: instantFromLocal(date, value.endMinute, timeZone),
                ...(value.note === "" ? {} : { note: value.note }),
                acknowledgedWarnings: [],
              })
              props.onSearchChange({ dialog: undefined })
            }}
          />
        ) : null}

        {props.search.dialog === "reschedule" && openAppointment !== null && brief !== null && result !== null ? (
          <RescheduleView
            appointment={openAppointment}
            brief={brief}
            result={result}
            date={date}
            timeZone={timeZone}
            nowMinute={nowMinute}
            submitting={actions.state.busy === "reschedule"}
            failure={actions.state.failure}
            onBack={() => {
              props.onSearchChange({ dialog: undefined })
            }}
            onSubmit={(value) => {
              actions.reschedule(openAppointment, value)
              props.onSearchChange({ dialog: undefined })
            }}
          />
        ) : null}

        {actions.state.conflict === null ? null : (
          <VersionConflictDialog
            clientName={actions.state.conflict.snapshot.client.displayName}
            changedBy={actions.state.conflict.changedBy}
            before={describeVersion(actions.state.conflict.snapshot, timeZone, employeeNameOf)}
            after={(() => {
              const fresh = appointments.find(
                (item) => item.id === actions.state.conflict?.snapshot.id,
              )
              return fresh === undefined
                ? null
                : describeVersion(fresh, timeZone, employeeNameOf)
            })()}
            repeatLabel={actions.state.conflict.repeatLabel}
            repeating={actions.state.busy !== null}
            onShowCurrent={actions.dismissConflict}
            onRepeat={() => {
              const conflict = actions.state.conflict
              if (conflict === null) return
              const fresh = appointments.find((item) => item.id === conflict.snapshot.id)
              const version = fresh?.updatedAt ?? conflict.currentUpdatedAt
              if (version === null || version === undefined) return
              conflict.repeat(version)
            }}
          />
        )}

        {actions.state.confirmation === null ? null : (
          <ConfirmWarningsDialog
            messages={actions.state.confirmation.messages}
            confirmLabel="Всё равно продолжить"
            onCancel={actions.dismissConfirmation}
            onConfirm={actions.state.confirmation.confirm}
          />
        )}

        {availability.blocking === null || maintenanceDismissed ? null : (
          <div className={styles["blocking"]}>
            <div className={styles["blockingBox"]}>
              <h2 className={styles["blockingTitle"]}>{availability.blocking.title}</h2>
              <p>{availability.blocking.text}</p>
              {availability.blocking.readHint === null ? null : (
                <>
                  <span className={styles["blockingHint"]}>
                    {availability.blocking.readHint}
                  </span>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setMaintenanceDismissed(true)
                    }}
                  >
                    {availability.blocking.readLabel}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/** Перенос: окна считаются из загруженного дня по ВЫБРАННОМУ в диалоге мастеру. */
function RescheduleView(props: {
  readonly appointment: CabinetAppointmentView
  readonly brief: AppointmentBrief
  readonly result: ScheduleResult
  readonly date: string
  readonly timeZone: string
  readonly nowMinute: number
  readonly submitting: boolean
  readonly failure: string | null
  readonly onBack: () => void
  readonly onSubmit: (value: {
    readonly startAt: string
    readonly employeeId: string | null
    readonly initiatedBy: "client" | "staff"
    readonly notifyClient: boolean
  }) => void
}): ReactNode {
  const [employeeId, setEmployeeId] = useState<string | null>(props.appointment.employeeId)

  /*
   * ДЕНЬ ПЕРЕНОСА ЖИВЁТ В ДИАЛОГЕ, А НЕ В ЖУРНАЛЕ. «Перенесите на завтра» — обычная
   * просьба в трубку, и раньше она стоила администратору трёх действий мимо карточки:
   * закрыть панель, перелистать день, найти запись заново. Поэтому выбранный день
   * хранится здесь, а расписание ЭТОГО дня подгружается отдельным запросом: свободные
   * окна считаются только по загруженному дню, и взять их из сегодняшнего значило бы
   * предложить занятое время.
   */
  const [day, setDay] = useState(props.date)
  const [dayResult, setDayResult] = useState<ScheduleResult | null>(null)
  const [dayLoading, setDayLoading] = useState(false)

  useEffect(() => {
    if (day === props.date) {
      setDayResult(null)
      setDayLoading(false)
      return
    }
    const controller = new AbortController()
    setDayLoading(true)
    void (async () => {
      try {
        const loaded = await fetchJournal(
          { branchId: props.result.branch.id, dateFrom: day, dateTo: day },
          controller.signal,
        )
        if (!controller.signal.aborted) setDayResult(loaded)
      } catch {
        // Отказ не ломает диалог: окон не будет, и об этом сказано словами ниже.
        if (!controller.signal.aborted) setDayResult(null)
      } finally {
        if (!controller.signal.aborted) setDayLoading(false)
      }
    })()
    return () => {
      controller.abort()
    }
  }, [day, props.date, props.result.branch.id])

  const dayScheduleResult = day === props.date ? props.result : dayResult

  const duration = Math.max(
    props.appointment.services.reduce((sum, item) => sum + item.durationMinutes, 0),
    15,
  )

  const windows =
    employeeId === null || dayScheduleResult === null
      ? []
      : freeWindows({
          result: dayScheduleResult,
          date: day,
          employeeId,
          durationMinutes: duration,
          stepMinutes: dayScheduleResult.branch.slotStepMinutes,
          /*
           * ПОРОГ СЧИТАЕТСЯ ОТ НАСТОЯЩЕГО «СЕЙЧАС», А НЕ ОТ ДНЯ ЖУРНАЛА. props.nowMinute —
           * это минуты от начала ОТКРЫТОГО дня (use-journal-clock): на завтрашнем дне
           * журнала он отрицательный, на вчерашнем — больше суток. Подставив его сюда,
           * диалог предлагал на сегодня утренние часы, прошедшие восемь часов назад
           * (и перенос уезжал в прошлое), а со вчерашнего дня — не предлагал ничего.
           */
          ...(day === todayInZone(props.timeZone)
            ? { notBeforeMinute: minutesOfDay(new Date(), props.timeZone) }
            : {}),
          /*
           * Окна берутся НА ВЕСЬ ДЕНЬ: восемь первых — это 09:00…10:45, то есть первые
           * два часа смены, а «перенесите на завтра на вечер» — самая частая просьба
           * в трубку. Диалог раскладывает их по частям дня и показывает по нескольку
           * из каждой, поэтому длинный список ему не мешает.
           */
          limit: 200,
        })

  /*
   * «Сегодня» — это НАСТОЯЩЕЕ сегодня по зоне филиала, а не день, открытый в журнале:
   * администратор листает дни вперёд, и подпись «Сегодня» на пятнице через неделю —
   * прямая неправда, по которой перенесут не туда. Когда журнал показывает другой день,
   * он остаётся третьим вариантом — под своей датой, одним касанием.
   */
  const today = todayInZone(props.timeZone)
  const dayOptions = [
    { date: today, label: "Сегодня" },
    { date: shiftDate(today, 1), label: "Завтра" },
    ...(props.date === today || props.date === shiftDate(today, 1)
      ? []
      : [{ date: props.date, label: formatDayMonth(props.date, props.timeZone) }]),
  ]

  return (
    <RescheduleDialog
      appointment={props.brief}
      currentEmployeeId={employeeId}
      employees={(dayScheduleResult ?? props.result).employees}
      windows={windows}
      day={day}
      dayOptions={dayOptions}
      onDayChange={setDay}
      loadingWindows={dayLoading}
      dateLabel={formatDateLong(day, props.timeZone)}
      submitting={props.submitting}
      failure={props.failure}
      onEmployeeChange={setEmployeeId}
      onBack={props.onBack}
      onSubmit={(value) => {
        props.onSubmit({
          startAt: instantFromLocal(day, value.startMinute, props.timeZone),
          employeeId: value.employeeId,
          initiatedBy: value.initiatedBy,
          notifyClient: value.notifyClient,
        })
      }}
    />
  )
}

/** «16:30 · Анна Соколова» — строка версии в диалоге конфликта. */
function describeVersion(
  appointment: CabinetAppointmentView,
  timeZone: string,
  employeeName: (id: string | null) => string,
): string {
  return `${formatTimeOfDay(appointment.startAt, timeZone)} · ${employeeName(appointment.employeeId)}`
}

function briefOf(
  appointment: CabinetAppointmentView,
  context: { readonly employeeName: string; readonly date: string; readonly timeZone: string },
): AppointmentBrief {
  const price = appointment.priceMinor
  const prepaid = appointment.prepaidMinor
  return {
    id: appointment.id,
    clientName: appointment.client.displayName,
    whenLabel: `${formatDateLong(context.date, context.timeZone)}, ${formatTimeOfDay(appointment.startAt, context.timeZone)}`,
    employeeName: context.employeeName,
    moneyLabel:
      price === null
        ? null
        : prepaid === null || prepaid === 0
          ? formatMoney(price)
          : `${formatMoney(price)}, предоплата ${formatMoney(prepaid)}`,
    prepaidMinor: prepaid,
    noShowCount: appointment.client.noShowCount,
    minutesUntilVisit: Math.round((Date.parse(appointment.startAt) - Date.now()) / 60_000),
    hasExecutor: appointment.employeeId !== null,
  }
}

/** Клиент записи в виде строки списка — для заготовки «Продублировать». */
function clientOf(appointment: CabinetAppointmentView): ClientListItem {
  return {
    id: appointment.client.id,
    displayName: appointment.client.displayName,
    phoneMasked: appointment.client.phoneMasked,
    phone: appointment.client.phone,
    tags: [...appointment.client.tags],
    lastVisitAt: null,
    visitsCount: appointment.client.visitsCount,
    noShowCount: appointment.client.noShowCount,
    isBlacklisted: appointment.client.isBlacklisted,
  }
}

/** Конец визита в минутах дня: «Продублировать» ставит следующую запись сразу за ним. */
function minuteOfEnd(appointment: CabinetAppointmentView, timeZone: string): number {
  const [hours, minutes] = formatTimeOfDay(appointment.endAt, timeZone).split(":")
  const value = Number(hours) * 60 + Number(minutes)
  return Number.isFinite(value) ? value : 0
}

/**
 * Длительность визита в минутах — для «Продублировать».
 *
 * Шаг сетки здесь не годится: повторяют визит на час, а не пятнадцатиминутную
 * ячейку, и черновик с чужой длительностью даёт и неверный интервал на экране,
 * и удержание не на то время.
 */
function durationOf(appointment: CabinetAppointmentView, timeZone: string): number {
  const start = formatTimeOfDay(appointment.startAt, timeZone).split(":")
  const end = formatTimeOfDay(appointment.endAt, timeZone).split(":")
  const from = Number(start[0]) * 60 + Number(start[1])
  const to = Number(end[0]) * 60 + Number(end[1])
  const value = to - from
  return Number.isFinite(value) && value > 0 ? value : 0
}

function roundToStep(minute: number, step: number): number {
  return Math.floor(minute / step) * step
}
